import { EndpointError } from '@iconik-io/iconik-api/src/client/response';
import {
  httpDeleteJobsByJobId,
  httpGetJobs,
} from '@iconik-io/iconik-api/src/services/jobs';
import { httpGetFaceRecognitionPersons } from '@iconik-io/iconik-api/src/services/ml';
import { httpPostSearch } from '@iconik-io/iconik-api/src/services/search';
import {
  isSearchObjectType,
  OBJECT_TYPE_PERSONS,
  SEARCH_OBJECT_TYPES,
} from '@iconik-io/iconik-api/src/types/object_types';
import { handleErrors } from '@iconik-io/shared-lib/src/utils/handle_messages';
import { getSortQuery } from '@iconik-io/shared-lib/src/utils/sort/get_sort_query';

import {
  ADMIN_JOBS_DASHBOARD_PAGE_ROUTE,
  ADMIN_JOBS_HISTORY_JOB_PAGE_ROUTE,
  ADMIN_JOBS_HISTORY_PAGE_ROUTE,
  ADMIN_JOBS_JOB_PAGE_ROUTE,
} from '../../../constants/routes';
import { selectJobsFiltersState } from '../../../panels/admin_filters_jobs/selectors';
import {
  getCurrentRouteParams,
  getCurrentRoutePattern,
} from '../../../router/current_route_params';
import type { AppDispatch, GetState } from '../../../store/types';
import { addOneSecondToRightBoundary } from '../../../utils/add_one_second_to_right_boundary';
import { getSearchDocument } from '../../../utils/get_search_document';
import { guardFilterRange } from '../../../utils/search_utils/guard_filter';
import { prepareQueryParameter } from '../../../utils/search_utils/search_utils';
import { commonListActions } from '../../common_list/slice';
import {
  type CommonListGetParams,
  wrapCommonDelete,
  wrapCommonGetList,
} from '../../common_list/thunks';
import { getJobWidgetsCommonListPrefix } from '../../common_list/utils';
import { historyPush } from '../../history/actions';

type JobsListGetParams = CommonListGetParams & {
  query?: string;
  date_created?: string;
  date_modified?: string;
  parent_id?: string;
};

const actions = commonListActions('admin_jobs_history');

export function getJobs(params: JobsListGetParams = {}, debounce?: boolean) {
  return (dispatch: AppDispatch, getState: GetState) =>
    wrapCommonGetList({
      prefix: 'admin_jobs_history',
      params,
      dispatch,
      debounce,
      getListPromise: (listParams) => {
        const state = getState();
        const pageState = state.pages.adminJobs;
        const filtersPanelState = state.panelsState.adminJobsFilters;
        const { automation_ids, show_automation_jobs, ...filters } =
          filtersPanelState.filterValues;

        if (filters.transcoder_id) {
          filters['metadata.transcoder_id'] = filters.transcoder_id;
        }

        if (show_automation_jobs === false) {
          filters._missing_ = 'metadata.automation_id';
        } else {
          filters['metadata.automation_id'] = automation_ids;
        }
        const dateCreated = guardFilterRange(filters.date_created);
        const dateModified = guardFilterRange(filters.date_modified);

        const { commonListPrefix } = selectJobsFiltersState(
          state,
          getCurrentRouteParams().id,
          getCurrentRoutePattern(),
        );
        const { resetListData } = commonListActions(commonListPrefix);

        return dispatch(
          httpGetJobs({
            sort: getSortQuery(pageState.sort),
            page: listParams.page,
            per_page: 50,
            parent_id: listParams.parent_id,
            ...filters,
            ...filtersPanelState.facetValues,
            query: prepareQueryParameter(filtersPanelState.query, {
              addAsteriskOnlyToEndOfQuery: true,
            }),
            date_created: dateCreated
              ? addOneSecondToRightBoundary(dateCreated)
              : undefined,
            date_modified: dateModified
              ? addOneSecondToRightBoundary(dateModified)
              : undefined,
          }),
        )
          .then((response) => {
            const ids: string[] = [];
            const personIds: string[] = [];
            response.body.objects.forEach((job) => {
              if (
                job.object_type &&
                isSearchObjectType(job.object_type) &&
                job.object_id
              ) {
                ids.push(job.object_id);
              }
              if (
                job.object_type === OBJECT_TYPE_PERSONS &&
                job.object_id &&
                job.type !== 'DELETE_PERSON'
              ) {
                personIds.push(job.object_id);
              }
            });
            if (ids.length > 0) {
              dispatch(
                httpPostSearch(
                  getSearchDocument(ids, [...SEARCH_OBJECT_TYPES]),
                  {
                    save_search_history: false,
                    per_page: 100,
                  },
                ),
              );
            }
            if (personIds.length > 0) {
              dispatch(
                // biome-ignore lint/plugin: the generated query types the ids as one string, which is what joining them produces.
                httpGetFaceRecognitionPersons({
                  ids: personIds.join(','),
                  per_page: personIds.length,
                } as Parameters<typeof httpGetFaceRecognitionPersons>[0]),
              ).catch(() => {
                // Best-effort enrichment fetch; swallow the rejection so it
                // doesn't surface as an unhandled promise rejection. Any
                // user-facing error is already handled by the global http
                // error middleware before this runs.
              });
            }
            return response;
          })
          .catch((error) => {
            if (error instanceof EndpointError) {
              const errors = handleErrors(error);
              dispatch(resetListData());
              dispatch(
                actions.setLatestError({
                  status: error.response.status,
                  statusText: error.response.statusText,
                  headers: error.response.headers,
                  errors,
                }),
              );
            }
            throw error;
          });
      },
    });
}

export function deleteJobsByIds(ids: string[]) {
  return (dispatch: AppDispatch, getState: GetState): Promise<void> => {
    return wrapCommonDelete({
      prefix: 'admin_jobs_history',
      dispatch,
      ids,
      deletePromise: Promise.all(
        ids.map((id) =>
          dispatch(httpDeleteJobsByJobId(id, { recursive: true })),
        ),
      ),
    })
      .then(() => {
        // Delete items for all widgets as well
        const state = getState();
        const jobsWidgetsIds = Object.keys(state.jobsWidgetsLists);
        jobsWidgetsIds.forEach((jobWidgetsId) => {
          const { deleteIds } = commonListActions(
            getJobWidgetsCommonListPrefix(jobWidgetsId),
          );
          dispatch(deleteIds(ids));
        });
      })
      .then(() => {
        // Redirect if in job details page
        const route = getState().routeParams.route;
        const isJobsHistoryJobPage =
          route === ADMIN_JOBS_HISTORY_JOB_PAGE_ROUTE;
        const isJobsJobPage = route === ADMIN_JOBS_JOB_PAGE_ROUTE;

        if ((isJobsHistoryJobPage || isJobsJobPage) && ids.length) {
          const redirectRoute = isJobsHistoryJobPage
            ? ADMIN_JOBS_HISTORY_PAGE_ROUTE
            : ADMIN_JOBS_DASHBOARD_PAGE_ROUTE;
          dispatch(historyPush(redirectRoute));
        }
      });
  };
}
